/**
 * Admin merchant customer oversight — closes gap #5 from the
 * admin-vs-merchant audit. Per-merchant customer list enriched with
 * LTV / churn / order stats via the shared customerMetricsService,
 * so admins can spot at-risk merchants (lots of DORMANT customers)
 * and high-value merchant-customer pairs.
 *
 * Route: /merchants/:merchantId/customers  (mounted in App.js under
 *        RequireAdmin + Shell + PermissionGuard stores.read).
 * CSS scoped with `mc-*` per CLAUDE.md.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './MerchantCustomers.css';

const api = (path, opts = {}) => {
  const base = process.env.REACT_APP_BACKEND_URL || '/api';
  return fetch(base + path, {
    credentials: 'include',
    ...opts,
    headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
  });
};

// Churn tier colors — kept in sync with customerMetricsService rules.
const CHURN_META = {
  NEW:          { color: '#0891b2', label: 'New' },
  NEVER_BOUGHT: { color: '#dc2626', label: 'Never bought' },
  ACTIVE:       { color: '#059669', label: 'Active' },
  AT_RISK:      { color: '#d97706', label: 'At risk' },
  DORMANT:      { color: '#6b7280', label: 'Dormant' },
  UNKNOWN:      { color: '#9ca3af', label: 'Unknown' },
};

function fmtNaira(v) {
  const n = Number(v) || 0;
  return `₦${n.toLocaleString('en-NG', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}
function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return '—'; }
}
function fmtDateTime(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return '—'; }
}
function fullName(c) {
  return [c?.firstName, c?.lastName].filter(Boolean).join(' ')
    || c?.email
    || 'Guest';
}
function initialsOf(c) {
  const name = fullName(c);
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('') || '?';
}

function ChurnPill({ tier }) {
  const m = CHURN_META[tier] || CHURN_META.UNKNOWN;
  return (
    <span className="mc-pill" style={{ background: `${m.color}18`, color: m.color }}>
      <span className="mc-pill__dot" style={{ background: m.color }} />
      {m.label}
    </span>
  );
}

/* ══════════════════════════════════════════════════════════════════════ */
/*  Main page                                                              */
/* ══════════════════════════════════════════════════════════════════════ */

export default function MerchantCustomers() {
  const { merchantId } = useParams();
  const navigate = useNavigate();

  const [rows, setRows]         = useState([]);
  const [summary, setSummary]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [detailId, setDetailId] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 0 });

  const [filters, setFilters] = useState({ search: '', churnRisk: '', tag: '' });

  const load = useCallback(async (opts = {}) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.set('page',  String(opts.page  || pagination.page));
      params.set('limit', String(opts.limit || pagination.limit));
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });

      const res  = await api(`/admin/merchants/${merchantId}/customers?${params.toString()}`);
      const json = await res.json();
      if (!res.ok || !json?.success) throw new Error(json?.error || `HTTP ${res.status}`);
      setRows(json.customers || []);
      setSummary(json.summary || null);
      setPagination((p) => ({ ...p, ...(json.pagination || {}) }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [merchantId, filters, pagination.page, pagination.limit]);

  useEffect(() => { load({ page: 1 }); }, [merchantId, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const setFilter = (key, value) => {
    setPagination((p) => ({ ...p, page: 1 }));
    setFilters((cur) => ({ ...cur, [key]: value }));
  };
  const clearFilters = () => setFilters({ search: '', churnRisk: '', tag: '' });
  const hasFilters = useMemo(() => Object.values(filters).some(Boolean), [filters]);

  return (
    <div className="mc-page">
      <header className="mc-page__head">
        <button type="button" className="mc-back" onClick={() => navigate(-1)}>← Back</button>
        <div>
          <h1 className="mc-page__title">Customers</h1>
          <p className="mc-page__subtitle">Lifetime value + churn risk per customer. Same math as the merchant surface.</p>
        </div>
      </header>

      {/* Summary cards */}
      {summary && (
        <div className="mc-summary">
          <SummaryCard label="This page" value={summary.pageSize} />
          <SummaryCard label="Order total (page)" value={summary.totalOrders.toLocaleString('en-NG')} />
          <SummaryCard label="LTV total (page)" value={fmtNaira(summary.totalLTV)} tone="ok" />
          <SummaryCard label="Active" value={(summary.byChurn?.ACTIVE || 0).toLocaleString('en-NG')} tone="ok" />
          <SummaryCard label="At risk" value={(summary.byChurn?.AT_RISK || 0).toLocaleString('en-NG')} tone="warn" />
          <SummaryCard label="Dormant" value={(summary.byChurn?.DORMANT || 0).toLocaleString('en-NG')} tone="muted" />
        </div>
      )}

      {/* Filters */}
      <div className="mc-filters">
        <input
          type="text"
          className="mc-input"
          placeholder="Search email / name / phone…"
          value={filters.search}
          onChange={(e) => setFilter('search', e.target.value)}
        />
        <select className="mc-select" value={filters.churnRisk} onChange={(e) => setFilter('churnRisk', e.target.value)}>
          <option value="">All churn tiers</option>
          <option value="ACTIVE">Active</option>
          <option value="AT_RISK">At risk</option>
          <option value="DORMANT">Dormant</option>
          <option value="NEW">New</option>
          <option value="NEVER_BOUGHT">Never bought</option>
        </select>
        <input
          type="text"
          className="mc-input mc-input--sm"
          placeholder="Filter by tag…"
          value={filters.tag}
          onChange={(e) => setFilter('tag', e.target.value)}
        />
        {hasFilters && (
          <button type="button" className="mc-btn mc-btn--ghost" onClick={clearFilters}>Clear</button>
        )}
        <button type="button" className="mc-btn" onClick={() => load()} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {error && <div className="mc-error">{error}</div>}

      {/* Table */}
      <div className="mc-table-wrap">
        <table className="mc-table">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Contact</th>
              <th>Churn</th>
              <th className="mc-num">Orders</th>
              <th className="mc-num">LTV</th>
              <th className="mc-num">AOV</th>
              <th>Last purchase</th>
              <th>Signed up</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr><td colSpan={9} className="mc-empty">No customers match these filters.</td></tr>
            )}
            {rows.map((c) => {
              const m = c.metrics || {};
              return (
                <tr key={c.id} className="mc-row" onClick={() => setDetailId(c.id)}>
                  <td>
                    <div className="mc-customer">
                      <span className="mc-avatar" aria-hidden="true">{initialsOf(c)}</span>
                      <div className="mc-customer__copy">
                        <strong>{fullName(c)}</strong>
                        {c.isGuest && <span className="mc-cell-meta">Guest</span>}
                        {c.tags?.length > 0 && (
                          <span className="mc-tags">
                            {c.tags.slice(0, 3).map((t) => (
                              <span key={t} className="mc-tag">{t}</span>
                            ))}
                            {c.tags.length > 3 && <span className="mc-tag mc-tag--more">+{c.tags.length - 3}</span>}
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div>{c.email || '—'}</div>
                    {c.phone && <div className="mc-cell-meta">{c.phone}</div>}
                  </td>
                  <td><ChurnPill tier={m.churnRisk} /></td>
                  <td className="mc-num">{m.orderCount ?? 0}</td>
                  <td className="mc-num"><strong>{fmtNaira(m.totalSpent)}</strong></td>
                  <td className="mc-num">{fmtNaira(m.avgOrderValue)}</td>
                  <td>
                    {m.lastPurchaseAt ? fmtDate(m.lastPurchaseAt) : <span className="mc-muted">Never</span>}
                    {m.daysSinceLastPurchase != null && (
                      <div className="mc-cell-meta">{m.daysSinceLastPurchase}d ago</div>
                    )}
                  </td>
                  <td>{fmtDate(c.createdAt)}</td>
                  <td><button type="button" className="mc-view">View →</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pagination.total > 0 && (
        <div className="mc-pagination">
          <span>
            Showing {(pagination.page - 1) * pagination.limit + 1}–
            {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total.toLocaleString('en-NG')}
          </span>
          <button
            type="button"
            className="mc-btn"
            disabled={pagination.page <= 1 || loading}
            onClick={() => { setPagination((p) => ({ ...p, page: p.page - 1 })); load({ page: pagination.page - 1 }); }}
          >Prev</button>
          <button
            type="button"
            className="mc-btn"
            disabled={pagination.page >= pagination.totalPages || loading}
            onClick={() => { setPagination((p) => ({ ...p, page: p.page + 1 })); load({ page: pagination.page + 1 }); }}
          >Next</button>
        </div>
      )}

      {detailId && (
        <CustomerDetailDrawer
          customerId={detailId}
          merchantId={merchantId}
          onClose={() => setDetailId(null)}
          navigate={navigate}
        />
      )}
    </div>
  );
}

function SummaryCard({ label, value, tone }) {
  return (
    <div className={`mc-summary-card ${tone ? `mc-summary-card--${tone}` : ''}`}>
      <div className="mc-summary-card__label">{label}</div>
      <div className="mc-summary-card__value">{value}</div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════ */
/*  Detail drawer                                                          */
/* ══════════════════════════════════════════════════════════════════════ */

function CustomerDetailDrawer({ customerId, merchantId, onClose, navigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoad] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoad(true);
      setError('');
      try {
        const res  = await api(`/admin/customers/${customerId}`);
        const json = await res.json();
        // adminCustomersPrisma returns 200 without a `success` flag.
        if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);
        // Endpoint returns flat shape { ...customer, metrics, orders, merchant, refund* }.
        // Older wrapped shape { success, data: { customer, metrics, recentOrders, merchant } }
        // still tolerated defensively.
        const payload = json?.data && json.data.customer
          ? {
              customer:     json.data.customer,
              metrics:      json.data.metrics,
              recentOrders: json.data.recentOrders,
              merchant:     json.data.merchant,
            }
          : {
              customer: json,
              metrics:  json.metrics,
              recentOrders: json.orders || [],
              merchant: json.merchant
                ? {
                    id: json.merchant.id,
                    email: json.merchant.email,
                    tier:  json.merchant.merchantTier || json.merchant.tier || null,
                    storeName: json.merchant.store?.name || json.merchant.storeName || null,
                    storeSlug: json.merchant.store?.slug || json.merchant.storeSlug || null,
                  }
                : null,
            };
        if (!cancelled) setData(payload);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoad(false);
      }
    })();
    return () => { cancelled = true; };
  }, [customerId]);

  const c = data?.customer;
  const m = data?.metrics;
  const recent = data?.recentOrders || [];
  const merchant = data?.merchant;

  return (
    <div className="mc-drawer-scrim" onClick={onClose}>
      <aside className="mc-drawer" onClick={(e) => e.stopPropagation()}>
        <header className="mc-drawer__head">
          <div>
            <h3>Customer</h3>
            {c && <p className="mc-drawer__sub">{fullName(c)} · {c.isGuest ? 'Guest' : 'Registered'}</p>}
          </div>
          <button type="button" className="mc-btn mc-btn--ghost" onClick={onClose}>Close</button>
        </header>

        <div className="mc-drawer__body">
          {loading && <div className="mc-loading">Loading…</div>}
          {error && <div className="mc-error">{error}</div>}
          {c && m && (
            <>
              {/* Identity */}
              <div className="mc-detail-block">
                <div className="mc-detail-kv"><span>Email</span><strong>{c.email || '—'}</strong></div>
                <div className="mc-detail-kv"><span>Phone</span><strong>{c.phone || '—'}</strong></div>
                <div className="mc-detail-kv"><span>Signed up</span><strong>{fmtDateTime(c.createdAt)}</strong></div>
                <div className="mc-detail-kv"><span>Last login</span><strong>{fmtDateTime(c.lastLogin)}</strong></div>
                <div className="mc-detail-kv"><span>Newsletter</span><strong>{c.newsletterSubscription ? 'Yes' : 'No'}</strong></div>
                <div className="mc-detail-kv"><span>Active</span><strong>{c.isActive ? 'Yes' : 'No'}</strong></div>
              </div>

              {/* Metrics */}
              <div className="mc-detail-block">
                <h4>Lifetime value</h4>
                <div className="mc-detail-kv"><span>Churn tier</span><strong><ChurnPill tier={m.churnRisk} /></strong></div>
                <div className="mc-detail-kv"><span>Total spent</span><strong>{fmtNaira(m.totalSpent)}</strong></div>
                <div className="mc-detail-kv"><span>Order count</span><strong>{m.orderCount}</strong></div>
                <div className="mc-detail-kv"><span>Avg order value</span><strong>{fmtNaira(m.avgOrderValue)}</strong></div>
                <div className="mc-detail-kv"><span>First purchase</span><strong>{fmtDate(m.firstPurchaseAt)}</strong></div>
                <div className="mc-detail-kv"><span>Last purchase</span><strong>{fmtDate(m.lastPurchaseAt)}{m.daysSinceLastPurchase != null ? ` (${m.daysSinceLastPurchase}d ago)` : ''}</strong></div>
              </div>

              {/* Tags & groups */}
              {(c.tags?.length > 0 || c.groups?.length > 0) && (
                <div className="mc-detail-block">
                  {c.tags?.length > 0 && (
                    <div className="mc-detail-tags">
                      <span className="mc-detail-tags__label">Tags</span>
                      <div className="mc-tags">{c.tags.map((t) => <span key={t} className="mc-tag">{t}</span>)}</div>
                    </div>
                  )}
                  {c.groups?.length > 0 && (
                    <div className="mc-detail-tags">
                      <span className="mc-detail-tags__label">Groups</span>
                      <div className="mc-tags">{c.groups.map((g) => <span key={g} className="mc-tag mc-tag--group">{g}</span>)}</div>
                    </div>
                  )}
                </div>
              )}

              {/* Recent orders */}
              <div className="mc-detail-block">
                <h4>Recent orders ({recent.length})</h4>
                {recent.length === 0 && <p className="mc-detail-empty">No orders yet.</p>}
                {recent.map((o) => (
                  <div key={o.id} className="mc-order-row">
                    <div>
                      <strong>{o.orderNumber || o.id.slice(0, 8)}</strong>
                      <div className="mc-cell-meta">{o.channel} · {o.status} · {o.paymentStatus}</div>
                    </div>
                    <div className="mc-order-row__amt">
                      <strong>{fmtNaira(o.total)}</strong>
                      <span className="mc-cell-meta">{fmtDate(o.paidAt || o.createdAt)}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Merchant identity */}
              {merchant && (
                <div className="mc-detail-block">
                  <h4>Merchant</h4>
                  <div className="mc-detail-kv"><span>Store</span><strong>{merchant.storeName || '—'}{merchant.storeSlug ? ` (/${merchant.storeSlug})` : ''}</strong></div>
                  <div className="mc-detail-kv"><span>Tier</span><strong>{merchant.tier || 'FREE'}</strong></div>
                  <div className="mc-actions">
                    <button
                      type="button"
                      className="mc-btn"
                      onClick={() => navigate(`/merchants/${merchantId}/orders`)}
                    >
                      View merchant orders
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </aside>
    </div>
  );
}
